import React, { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import type { Dispatch } from '@tmapy/redux';
import { useMessage, useSelectMessages } from '@tmapy/intl';
import { unaccentAndLower } from '@tmapy/beetlejuice-utils';

import {
  BtnBar,
  Form,
  FormItem,
  Input,
  CtaBtn,
  SecondaryBtn,
  StickyFooter,
  MultiSelect,
  MultiSelectProps,
  Select,
} from '@tmapy/style-guide';

import type { OmittedFormItemProps } from '../../types';
import { useInput, useMultiSelect } from '../../hooks';
import { setSubjectListFilter } from '../../redux/filter/actions';

import { SubjektyListQuery, SubjektyListResponse } from '../../openapi/operations/subjektyList';
import { RegistryEnum } from '../../openapi/types/RegistryEnum';
import { TypOsobyEnum } from '../../openapi/types/TypOsobyEnum';

import {
  registryMessages,
  subjectBtnMessages,
  subjectMessages,
  subjectPersonMessages,
} from './subjectMessages';

type SubjectFiltersProps = {
  filters: SubjektyListQuery;
  dispatch: Dispatch;
};

export const SubjectFilter: React.FC<SubjectFiltersProps> = ({ filters, dispatch }) => {
  const formatMessage = useMessage();

  const search = useInput({
    name: 'search',
    initialValue: filters.search,
    label: formatMessage(subjectMessages.search),
  });

  const personItems = useSelectMessages(subjectPersonMessages, TypOsobyEnum);
  const typ_osoby = useInput({
    name: 'typ_osoby',
    initialValue: filters.typ_osoby,
    items: personItems,
    label: formatMessage(subjectMessages.person),
  });

  const ico = useInput({
    name: 'ico',
    initialValue: filters.ico__icontains,
    label: formatMessage(subjectMessages.ico),
  });

  const id_subjektu = useInput({
    name: 'id_subjektu',
    initialValue: filters.id_subjektu__icontains,
    label: formatMessage(subjectMessages.id),
  });

  const note = useInput({
    name: 'note',
    initialValue: filters.poznamka__icontains,
    label: formatMessage(subjectMessages.note),
  });

  // REGISTRY
  const registryItems = useSelectMessages(registryMessages, RegistryEnum);

  const prepareRegistryItems = useCallback(
    (val: RegistryEnum) => {
      return registryItems.filter(
        (register) => unaccentAndLower(register.label).indexOf(unaccentAndLower(val)) >= 0,
      );
    },
    [registryItems],
  );

  const registryInitialValues = useMemo(() => {
    return filters.registry?.length
      ? registryItems.filter((register) => filters.registry!.includes(register.value))
      : [];
  }, [registryItems, filters.registry]);

  const registry = useMultiSelect<
    MultiSelectProps<SubjektyListResponse> | OmittedFormItemProps,
    (typeof registryItems)[0]
  >({
    name: 'registry',
    label: formatMessage(subjectMessages.registry),
    initialValue: registryInitialValues,
    prepareItems: prepareRegistryItems,
    renderValue: (state: (typeof registryItems)[0]) => state.label,
    renderItem: (state: (typeof registryItems)[0]) => state.label,
  });

  // CLEAR FORM
  const filterClearScope = [search, ico, id_subjektu, note, registry, typ_osoby];
  const handleClearBtnClick = useCallback(() => {
    filterClearScope.forEach((formItem) => formItem?.clear?.());
    dispatch(setSubjectListFilter({}));
  }, [dispatch]);

  // SUBMIT FORM
  const handleSubmit = () => {
    dispatch(
      setSubjectListFilter({
        search: search.value,
        poznamka__icontains: note.value,
        ico__icontains: ico.value,
        id_subjektu__icontains: id_subjektu.value,
        registry: registry.values.map((register) => register.value),
        typ_osoby: typ_osoby.value,
      }),
    );
  };

  return (
    <Form onSubmit={handleSubmit}>
      <div className='sg-u-vs-2'>
        <FormItem {...search}>
          <Input {...search} />
        </FormItem>
        <FormItem {...typ_osoby}>
          <Select hasEmptyItem {...typ_osoby} />
        </FormItem>
        <FormItem {...ico}>
          <Input {...ico} />
        </FormItem>
        <FormItem {...id_subjektu}>
          <Input {...id_subjektu} />
        </FormItem>
        <FormItem {...registry}>
          <MultiSelect {...registry} />
        </FormItem>
        <FormItem {...note}>
          <Input {...note} />
        </FormItem>
      </div>
      <StickyFooter>
        <BtnBar stretch>
          <CtaBtn type='submit'>
            <FormattedMessage {...subjectBtnMessages.filterSubmit} />
          </CtaBtn>
          <SecondaryBtn isDanger onClick={handleClearBtnClick}>
            <FormattedMessage {...subjectBtnMessages.clear} />
          </SecondaryBtn>
        </BtnBar>
      </StickyFooter>
    </Form>
  );
};
